/**
 * Auth storage (ADR-0036 — supersedes the per-tab model of 2026-06-20).
 *
 * **The Penn access token is never written to any web store.** It lives in the
 * module-level `accessTokenStore` below, which dies with the page. The session
 * itself is carried by the `penn_rt` HttpOnly cookie, which no script can read
 * and which spans tabs — so a new tab restores by calling `/v1/auth/refresh`,
 * not by reading a token someone left in a store.
 *
 * The old model (Penn JWT + user in `sessionStorage`) was a deliberate decision
 * — "log out as soon as the browser is closed" — and it did achieve that. What
 * it also achieved was a logout on every new tab, every crash-restore and every
 * browser restart, plus a script-readable 8-day bearer token. This ticket
 * reverses the decision; expiry is now enforced server-side by `auth_sessions`
 * (12 h absolute / 8 h idle) instead of by the browser closing.
 *
 * `authStorage` is retained with the same shape so every existing import keeps
 * working, and it still uses `sessionStorage` for NON-credential UI state
 * (cached user for the `x-auth-scope` header, selected region, Work-For
 * context). Those are conveniences, not credentials: none of them grants access
 * on its own, and the server re-authorises from the DB on every refresh. Reads
 * and writes of the token key are transparently redirected to memory, so a
 * forgotten call site cannot leak the credential back into a web store.
 *
 * Falls back to an in-memory no-op store when no DOM is present (tests without
 * jsdom, SSR) so importing this module never throws.
 */
import { TOKEN_STORAGE_KEY } from "./auth-storage-keys";

type SessionStore = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const noopStore: SessionStore = {
  getItem: () => null,
  setItem: () => undefined,
  removeItem: () => undefined,
};

const webStore: SessionStore =
  typeof window !== "undefined" && window.sessionStorage
    ? window.sessionStorage
    : noopStore;

/**
 * The in-memory access-token holder. Module scope, so it is per-page-load and
 * per-tab and is gone the instant the tab navigates away or is closed. No
 * `localStorage`, no `sessionStorage`, nothing an XSS payload can read out of
 * a store after the fact.
 */
const accessTokenStore = {
  token: null as string | null,
};

export function getAccessToken(): string | null {
  return accessTokenStore.token;
}

export function setAccessToken(token: string | null): void {
  accessTokenStore.token = token;
}

export function clearAccessToken(): void {
  accessTokenStore.token = null;
}

/**
 * Non-credential session state, still in `sessionStorage`. The token key is
 * intercepted and served from memory — see the module comment.
 */
export const authStorage: SessionStore = {
  getItem: (key: string) =>
    key === TOKEN_STORAGE_KEY ? accessTokenStore.token : webStore.getItem(key),
  setItem: (key: string, value: string) => {
    if (key === TOKEN_STORAGE_KEY) {
      accessTokenStore.token = value;
      return;
    }
    webStore.setItem(key, value);
  },
  removeItem: (key: string) => {
    if (key === TOKEN_STORAGE_KEY) {
      accessTokenStore.token = null;
      return;
    }
    webStore.removeItem(key);
  },
};
